import { api, serverApi } from '@/lib/utils/api';
import type { AxiosRequestConfig } from 'axios';

// Minimal typed wrappers around the Horodaty API (via Laravel gateway)

export type LoginResponse = {
    access_token: string;
    expires_at?: string;
};

export type ApiResponse<T> = {
    data: T;
};

export type FolderSignatureType = 'SIGNATURE_CLIENT' | 'SIGNATURE_OWNER';

/**
 * Payload polymorphe accepté par les endpoints "pictures" pour photo ou vidéo.
 * - Photo : renseigner `base64` (JSON).
 * - Vidéo : renseigner `video` (Blob/File) + `mime_type` + `duration_ms` (multipart).
 */
export type MediaUploadPayload = {
    file_name: string;
    latitude?: number;
    longitude?: number;
    field_id?: string | number;
    base64?: string;
    video?: Blob | File;
    mime_type?: string;
    duration_ms?: number;
};

/** Construit une requête JSON (photo) ou multipart (vidéo) selon le payload. */
function postMedia(url: string, payload: MediaUploadPayload, config: AxiosRequestConfig = {}) {
    if (payload.video) {
        const form = new FormData();
        form.append('file_name', payload.file_name);
        if (payload.latitude !== undefined) form.append('latitude', String(payload.latitude));
        if (payload.longitude !== undefined) form.append('longitude', String(payload.longitude));
        if (payload.field_id !== undefined) form.append('field_id', String(payload.field_id));
        if (payload.mime_type) form.append('mime_type', payload.mime_type);
        if (payload.duration_ms !== undefined) form.append('duration_ms', String(payload.duration_ms));
        form.append('video', payload.video, payload.file_name);
        return api.post(url, form, { timeout: 120_000, ...config });
    }
    return api.post(url, payload, config);
}

export const horodatyApi = {
    // Public
    ping() {
        return api.get('/api/ping');
    },

    // Auth
    login(payload: { email: string; password: string }) {
        // Stores token in PHP session (server-side)
        return serverApi.post<LoginResponse>('/session/auth/login', payload);
    },

    logout() {
        return serverApi.post('/session/auth/logout');
    },

    register(payload: {
        email: string;
        password: string;
        confirm_password: string;
        auto_login?: boolean;
    }) {
        // Stores token in PHP session (server-side)
        return serverApi.post('/session/auth/register', payload);
    },

    registerPro(payload: {
        email: string;
        password: string;
        confirm_password: string;
        company: {
            name: string;
            siret?: string;
            vat?: string;
        };
        auto_login?: boolean;
    }) {
        console.log(payload);
        
        // Stores token in PHP session (server-side)
        return serverApi.post('/session/auth/register/pro', payload);
    },

    google(payload: { token: string }) {
        return api.post('/api/auth/google', payload);
    },

    resetPassword(payload: { email: string; source?: 'v2' | 'mobile' }) {
        return api.post('/api/auth/reset-password', payload);
    },

    resetPasswordConfirm(payload: {
        token: string;
        email: string;
        password: string;
        password_confirmation: string;
    }) {
        return api.post('/api/auth/reset-password-confirm', payload);
    },

    createPassword(payload: {
        token: string;
        email: string;
        password: string;
        password_confirmation: string;
    }) {
        return api.post('/api/auth/create-password', payload);
    },

    verifyEmail(params: { id: string; hash: string; expires: string; signature: string }) {
        return serverApi.get<LoginResponse>('/session/auth/verify-email', { params });
    },

    // User
    getUser<T = unknown>() {
        return api.get<ApiResponse<T>>('/api/user');
    },

    updateUser<T = unknown>(payload: { first_name?: string; last_name?: string }) {
        return api.patch<ApiResponse<T>>('/api/user/update', payload);
    },

    updatePassword(payload: {
        old_password: string;
        new_password: string;
        confirm_password: string;
    }) {
        return api.patch('/api/user/update-password', payload);
    },

    updateEmail<T = unknown>(payload: { email: string; password: string }) {
        return api.patch<ApiResponse<T>>('/api/user/update-email', payload);
    },

    updateBilling<T = unknown>(payload: {
        first_name: string;
        last_name: string;
        facturation_street?: string;
        facturation_cp?: string;
        facturation_city?: string;
    }) {
        return api.patch<ApiResponse<T>>('/api/user/update-billing', payload);
    },

    getSettings<T = unknown>() {
        return api.get<T>('/api/user/settings');
    },

    updateSettings<T = unknown>(payload: Record<string, unknown>) {
        return api.patch<T>('/api/user/settings', payload);
    },

    toggleFavoriteProcedure<T = unknown>(procedureId: string) {
        return api.post<T>('/api/user/settings/favorite-procedure', { procedure_id: procedureId });
    },

    /**
     * Enregistre la langue choisie sur le compte, pour la retrouver sur les
     * autres appareils. Endpoint distinct d'updateSettings() : celui-ci remet
     * aux valeurs par défaut tout champ absent du payload.
     */
    updateLocale<T = unknown>(locale: string) {
        return api.patch<T>('/api/user/settings/locale', { locale });
    },

    getStatus<T = unknown>() {
        return api.get<T>('/api/user/status');
    },

    deleteAccount() {
        return api.post('/api/user/delete-account');
    },

    sendEmailVerification() {
        return api.post('/api/user/email/verification-notification');
    },

    // Folders
    listFolders(params?: {
        all?: number | boolean;
        days?: number;
        archived?: number | boolean;
        search?: string;
        city?: string;
        reference?: string;
    }) {
        return api.get('/api/folders', { params });
    },

    /**
     * Récupérer l'arborescence complète des dossiers et sous-dossiers en une seule requête
     * Retourne { folders: [...], subfolders: [...] } avec les relations parent/enfant
     * Avec recent=true, retourne aussi recent_items (liste unifiée triée par updated_at)
     */
    getFoldersTree(params?: { recent?: boolean; limit?: number }) {
        return api.get<{
            folders: Array<{
                uniq_id: string;
                name: string;
                ref: string;
                type: string;
                owned_by?: string;
                updated_at: string;
                created_at: string;
            }>;
            subfolders: Array<{
                uniq_id: string;
                name: string;
                ref: string;
                type: string;
                folder_uniq_id: string;
                parent_subfolder_uniq_id: string | null;
                updated_at: string;
                created_at: string;
            }>;
            recent_items?: Array<{
                uniq_id: string;
                name: string;
                ref: string;
                type: string;
                folder_uniq_id: string | null;
                parent_subfolder_uniq_id: string | null;
                updated_at: string;
                created_at: string;
            }>;
        }>('/api/folders/tree', { params });
    },

    createFolder(payload: {
        // API expects `nom` (FR). We also accept legacy `name` for UI convenience.
        nom?: string;
        name?: string;
        reference?: string;
        rue?: string;
        rue_2?: string;
        code_postal?: number;
        ville?: string;
        latitude?: number;
        longitude?: number;
        external_ref?: string;
        notes?: string;
        icon?: string;
        'date-intervention'?: string;
        'user-to-affect'?: string;
        'folders-model'?: string;
        status?: string | null;
        mandatory_procedures?: string[];
    }) {
        const nom = payload.nom ?? payload.name;
        return api.post('/api/folders', {
            ...payload,
            nom,
        });
    },

    getFolder(id: string | number) {
        return api.get(`/api/folders/${id}`);
    },

    getFolderByExternalRef(externalRef: string) {
        return api.get(`/api/folders/external/${encodeURIComponent(externalRef)}`);
    },

    updateFolder(
        id: string | number,
        payload: {
            name: string;
            street_1?: string;
            street_2?: string;
            cp?: number | string;
            city?: string;
            latitude?: number;
            longitude?: number;
            notes?: string;
            // Not explicitly documented in ROUTES.md update payload, but commonly supported.
            reference?: string;
            external_ref?: string;
            'user-to-affect'?: string;
            status?: string | null;
        }
    ) {
        return api.patch(`/api/folders/${id}`, payload);
    },

    archiveFolder(id: string | number) {
        return api.patch(`/api/folders/${id}/archive`);
    },

    restoreFolder(id: string | number) {
        return api.patch(`/api/folders/${id}/restore`);
    },

    archiveSubfolder(folderId: string | number, subfolderId: string | number) {
        return api.patch(`/api/folders/${folderId}/interventions/${subfolderId}/archive`);
    },

    restoreSubfolder(folderId: string | number, subfolderId: string | number) {
        return api.patch(`/api/folders/${folderId}/interventions/${subfolderId}/restore`);
    },

    uploadFolderPicture(
        id: string | number,
        payload: { file_name: string; latitude: number; longitude: number; base64: string }
    ) {
        return api.post(`/api/folders/${id}/pictures`, payload);
    },

    generateFolderPdf(id: string | number) {
        return api.post(`/api/folders/${id}/generate-pdf`);
    },

    downloadFolderLastReport(id: string | number) {
        return api.get(`/api/folders/${id}/last-report`, { responseType: 'blob' });
    },

    getFoldersWithReports() {
        return api.get('/api/folders/with-reports');
    },

    exportFolderReportsZip(folderIds: string[], subfolderIds: string[]) {
        return api.post('/api/folders/export-reports-zip', { folder_ids: folderIds, subfolder_ids: subfolderIds }, { responseType: 'blob' });
    },

    listFolderModels() {
        return api.get('/api/folders/models');
    },

    addFolderSignature(id: string | number, payload: { type: FolderSignatureType; base64: string }) {
        return api.post(`/api/folders/${id}/signatures`, payload);
    },

    getFolderSignature(id: string | number, type: FolderSignatureType) {
        return api.get(`/api/folders/${id}/signatures/${type}`, { responseType: 'blob' });
    },

    deleteFolderSignature(id: string | number, type: FolderSignatureType) {
        return api.delete(`/api/folders/${id}/signatures/${type}`);
    },

    createFolderClient(
        id: string | number,
        payload: { first_name: string; last_name: string; email: string; tel?: string }
    ) {
        return api.post(`/api/folders/${id}/clients`, payload);
    },

    attachFolderClient(id: string | number, payload: { client_id: string | number }) {
        return api.patch(`/api/folders/${id}/clients`, payload);
    },

    updateFolderClientInfos(
        folderId: string | number,
        clientId: string | number,
        payload: { first_name: string; last_name: string; email: string; tel?: string }
    ) {
        return api.patch(`/api/folders/${folderId}/clients/${clientId}/infos`, payload);
    },

    // Interventions
    listInterventions(folderId: string | number) {
        return api.get(`/api/folders/${folderId}/interventions`);
    },

    createIntervention(
        folderId: string | number,
        payload: { name: string; latitude?: number; longitude?: number; parent_subfolder_id?: string; user_uniq_id?: string; status?: string | null; mandatory_procedures?: string[] }
    ) {
        return api.post(`/api/folders/${folderId}/interventions`, payload);
    },

    /**
     * Créer une intervention de type procédure (rapport)
     * @param folderId - ID du dossier parent
     * @param payload - Données de la procédure
     * @param payload.name - Nom du rapport
     * @param payload.template_id - uniq_id du template
     * @param payload.answers - Réponses au formulaire
     * @param payload.parent_subfolder_id - ID du sous-dossier parent (optionnel)
     * @param payload.latitude - Latitude (optionnel)
     * @param payload.longitude - Longitude (optionnel)
     */
    createProcedureIntervention(
        folderId: string | number,
        payload: {
            name: string;
            template_id: string; // uniq_id du template
            answers: {
                sections: Array<{
                    id: string | number;
                    fields: Array<{
                        id: string | number;
                        type: string;
                        value: string | string[] | null;
                    }>;
                }>;
            };
            parent_subfolder_id?: string;
            latitude?: number;
            longitude?: number;
        }
    ) {
        return api.post(`/api/folders/${folderId}/interventions`, {
            ...payload,
            type: 'procedure',
        });
    },

    /**
     * Créer un rapport de procédure dans un dossier existant
     * Route: POST /api/folders/{folder_uid}/procedure-reports/create
     * Note: Utilise serverApi car c'est une route locale horodaty-v2
     */
    createProcedureReportInExistingFolder(
        folderId: string,
        payload: {
            name: string;
            template_id: string;
            answers: {
                sections: Array<{
                    id: string | number;
                    fields: Array<{
                        id: string | number;
                        type: string;
                        value: string | string[] | null;
                    }>;
                }>;
            };
            type?: string;
        }
    ) {
        return serverApi.post(`/folders/${folderId}/procedure-reports/create`, {
            ...payload,
            type: 'procedure',
        });
    },

    /**
     * Créer un rapport de procédure avec un nouveau dossier
     * Route: POST /api/procedure-reports/create-from-new-folder
     * Note: Utilise serverApi car c'est une route locale horodaty-v2
     */
    createProcedureReportWithNewFolder(
        payload: {
            folder: {
                nom: string;
            };
            procedure: {
                name: string;
                template_id: string;
                answers: {
                    sections: Array<{
                        id: string | number;
                        fields: Array<{
                            id: string | number;
                            type: string;
                            value: string | string[] | null;
                        }>;
                    }>;
                };
                type?: string;
            };
        }
    ) {
        return serverApi.post('/procedure-reports/create-from-new-folder', payload);
    },

    /**
     * Créer un brouillon de rapport de procédure
     * Le brouillon permet d'uploader des photos immédiatement avec horodatage correct
     */
    createProcedureReportDraft(
        folderId: string,
        payload: {
            name: string;
            template_id: string;
            latitude?: number;
            longitude?: number;
            parent_subfolder_id?: string;
        }
    ) {
        return serverApi.post(`/folders/${folderId}/interventions/draft`, payload);
    },

    /**
     * Finaliser un brouillon de rapport de procédure
     * Valide les réponses et passe le rapport en status "completed"
     */
    finalizeProcedureReportDraft(
        folderId: string,
        interventionId: string,
        payload: {
            answers: {
                sections: Array<{
                    id: string | number;
                    fields: Array<{
                        id: string | number;
                        type: string;
                        value: string | string[] | null;
                    }>;
                }>;
            };
            name?: string;
        }
    ) {
        return serverApi.patch(`/folders/${folderId}/interventions/${interventionId}/finalize`, payload);
    },

    /**
     * Mettre à jour un rapport de procédure existant
     */
    updateProcedureReport(
        folderId: string,
        interventionId: string,
        payload: {
            answers: {
                sections: Array<{
                    id: string | number;
                    fields: Array<{
                        id: string | number;
                        type: string;
                        value: string | string[] | null;
                    }>;
                }>;
            };
            name?: string;
        }
    ) {
        return serverApi.patch(`/folders/${folderId}/interventions/${interventionId}`, payload);
    },

    /**
     * Supprimer un brouillon de rapport de procédure
     * Supprime le rapport et ses photos associées
     */
    deleteProcedureReportDraft(folderId: string, interventionId: string) {
        return serverApi.delete(`/folders/${folderId}/interventions/${interventionId}/draft`);
    },

    getIntervention(folderId: string | number, interventionId: string | number) {
        return api.get(`/api/folders/${folderId}/interventions/${interventionId}`);
    },

    /**
     * Récupérer les sous-dossiers enfants d'une intervention (récursivité)
     */
    listInterventionChildren(folderId: string | number, interventionId: string | number) {
        return api.get(`/api/folders/${folderId}/interventions/${interventionId}/children`);
    },

    updateIntervention(
        folderId: string | number,
        interventionId: string | number,
        payload: { name?: string; latitude?: number; longitude?: number; user_uniq_id?: string; status?: string | null; mandatory_procedures?: string[] }
    ) {
        return api.patch(`/api/folders/${folderId}/interventions/${interventionId}`, payload);
    },

    updateChoice(
        folderId: string | number,
        interventionId: string | number,
        payload: { uniq_id: string; value: string; selected: boolean }
    ) {
        return api.patch(`/api/folders/${folderId}/interventions/${interventionId}/choice`, payload);
    },

    downloadInterventionZip(folderId: string | number, interventionId: string | number) {
        return api.get(`/api/folders/${folderId}/interventions/${interventionId}/download`, {
            responseType: 'blob',
        });
    },

    listInterventionPictures(folderId: string | number, interventionId: string | number) {
        return api.get(`/api/folders/${folderId}/interventions/${interventionId}/pictures`);
    },

    /**
     * Upload une photo OU une vidéo pour une intervention.
     * Pour une photo : fournir `base64`.
     * Pour une vidéo : fournir `video` (Blob/File) ainsi que `mime_type` et `duration_ms`.
     */
    uploadInterventionPicture(
        folderId: string | number,
        interventionId: string | number,
        payload: MediaUploadPayload
    ) {
        return postMedia(
            `/api/folders/${folderId}/interventions/${interventionId}/pictures`,
            payload
        );
    },

    getInterventionPicture(
        folderId: string | number,
        interventionId: string | number,
        pictureId: string | number,
        params?: { details?: boolean; format?: 'thumbnail' | 'mini' | 'fullscreen' | 'original'; band?: boolean }
    ) {
        if (params?.details) {
            return api.get(
                `/api/folders/${folderId}/interventions/${interventionId}/pictures/${pictureId}`,
                { params: { ...params, details: 'true' } }
            );
        }

        return api.get(`/api/folders/${folderId}/interventions/${interventionId}/pictures/${pictureId}`, {
            params,
            responseType: 'blob',
        });
    },

    moveInterventionPicture(
        sourceFolderId: string | number,
        sourceInterventionId: string | number,
        pictureId: string | number,
        payload: { folder: string; intervention?: string }
    ) {
        return api.patch(
            `/api/folders/${sourceFolderId}/interventions/${sourceInterventionId}/pictures/${pictureId}`,
            payload
        );
    },

        getInterventionPictureStatus(folderId: string | number, interventionId: string | number, pictureId: string | number) {
            return api.get(`/api/folders/${folderId}/interventions/${interventionId}/pictures/${pictureId}/status`);
        },

        getInterventionPictureCertificate(
            folderId: string | number,
            interventionId: string | number,
            pictureId: string | number,
            certificateId: string | number,
            params?: { details?: boolean }
        ) {
            if (params?.details) {
                return api.get(
                    `/api/folders/${folderId}/interventions/${interventionId}/pictures/${pictureId}/certificate/${certificateId}`,
                    { params: { ...params, details: 'true' } }
                );
            }

            return api.get(
                `/api/folders/${folderId}/interventions/${interventionId}/pictures/${pictureId}/certificate/${certificateId}`,
                { responseType: 'blob' }
            );
        },

        getInterventionPictureCertificateStatus(
            folderId: string | number,
            interventionId: string | number,
            pictureId: string | number,
            certificateId: string | number
        ) {
            return api.get(
                `/api/folders/${folderId}/interventions/${interventionId}/pictures/${pictureId}/certificate/${certificateId}/status`
            );
        },

    // Pictures
    /**
     * Crée une photo OU une vidéo horodatée (galerie utilisateur).
     * Pour une photo : fournir `base64`.
     * Pour une vidéo : fournir `video` (Blob/File) ainsi que `mime_type` et `duration_ms`.
     */
    createPicture(payload: MediaUploadPayload) {
        return postMedia('/api/pictures', payload);
    },

    listPictures() {
        return api.get('/api/pictures');
    },

    /**
     * Récupérer les détails d'une photo par son ID unique
     * Retourne folder_uniq_id et intervention_uniq_id
     */
    getPicture(id: string | number, params?: { details?: boolean; format?: 'thumbnail' | 'mini' | 'fullscreen' | 'original'; band?: boolean }) {
        if (params?.details) {
            return api.get(`/api/pictures/${id}`, { params: { ...params, details: 'true' } });
        }
        return api.get(`/api/pictures/${id}`, { params, responseType: 'blob' });
    },

    getPictureStatus(id: string | number) {
        return api.get(`/api/pictures/${id}/status`);
    },

    archivePicture(id: string | number) {
        return api.patch(`/api/pictures/${id}/archive`);
    },

    restorePicture(id: string | number) {
        return api.patch(`/api/pictures/${id}/restore`);
    },

    // Certificates
    listCertificates() {
        return api.get('/api/certificates');
    },

    getCertificate(id: string | number, params?: { details?: boolean }) {
        if (params?.details) {
            return api.get(`/api/certificates/${id}`, { params });
        }

        return api.get(`/api/certificates/${id}`, { responseType: 'blob' });
    },

    getCertificateStatus(id: string | number) {
        return api.get(`/api/certificates/${id}/status`);
    },

    markCertificateRead(id: string | number) {
        return api.patch(`/api/certificates/${id}/read-status`);
    },

    /**
     * Vérifie publiquement un certificat photo à partir de son numéro unique
     * (`id_certif`) et de sa clé d'accès (`access_code`), puis retourne le PDF
     * (blob) du certificat.
     *
     * Route publique : le gateway proxy injecte automatiquement `key_app`,
     * aucun token utilisateur n'est requis.
     *
     * En cas de clé incorrecte, l'API renvoie un statut 409 avec un corps JSON :
     *   { message, code: "ERR_PCC_0002", data: { remaining_attempts } }
     */
    verifyCertificate(payload: { number: string; accessKey: string }) {
        const form = new FormData();
        form.append('id_certif', payload.number);
        form.append('access_code', payload.accessKey);

        return api.post('/api/public/certificate', form, {
            responseType: 'blob',
        });
    },

    // Company manager
    managerUsers(params?: { search?: string; per_page?: number }) {
        return api.get('/api/company-manager/users', { params });
    },

    managerGetAvailableRoles() {
        return api.get<ApiResponse<any[]>>('/api/company-manager/users/availables-roles-creation');
    },

    // Permissions management
    managerGetPermissions(scope?: 'SELF' | 'DESCENDANTS') {
        return api.get<ApiResponse<any>>('/api/company-manager/permissions', {
            params: scope ? { scope } : undefined,
        });
    },

    managerTogglePermission(role: string, permission: string, scope: 'SELF' | 'DESCENDANTS', value: boolean) {
        return api.patch('/api/company-manager/permissions', { role, permission, scope, value });
    },

    managerBatchTogglePermissions(role: string, permissions: Array<{ permission: string; scope: 'SELF' | 'DESCENDANTS'; value: boolean }>) {
        return api.put('/api/company-manager/permissions', { role, permissions });
    },

    managerArchiveUser(user_id: string) {
        return api.patch<ApiResponse<any[]>>('companies/users/' + user_id + '/archive');
    },

    managerRestoreUser(user_id: string) {
        return api.patch<ApiResponse<any[]>>('companies/users/' + user_id + '/restore');
    },
    
    managerCreateUserV2(payload: {
        first_name: string;
        last_name: string;
        email: string;
        role?: string;
    }) {
        return api.post('/api/companies/users/v2', payload);
    },

    managerFolders(params?: { user_id?: string | number; search?: string }) {
        return api.get('/api/company-manager/folders', { params });
    },

    managerCreateFolder(payload: {
        name: string;
        user_id: string | number;
        city?: string;
        notes?: string;
    }) {
        return api.post('/api/company-manager/folders', payload);
    },

    managerInterventions(params?: { folder_id?: string | number; search?: string }) {
        return api.get('/api/company-manager/interventions', { params });
    },

    managerGetIntervention(folder_id: string | number, id: string | number) {
        return api.get(`/api/folders/${folder_id}/interventions/${id}`);
    },

    managerPictures(params?: { intervention_id?: string | number; search?: string; per_page?: number; page?: number }) {
        return api.get('/api/company-manager/pictures', { params });
    },

    managerMarkPictureRead(id: string | number, payload: { is_read: boolean }) {
        return api.patch(`/api/company-manager/pictures/${id}/read-status`, payload);
    },

    managerUpdateCompany(payload: {
        nom?: string;
        description?: string;
        notes?: string;
        rue?: string;
        code_postal?: string;
        ville?: string;
        siret?: string;
        tva?: string;
        phone?: string;
        email?: string;
        billing_email?: string;
        billing_id?: string;
    }) {
        return api.patch('/api/company-manager/company', payload);
    },

    // Stripe
    stripeProducts() {
        return api.get('/api/stripe/checkout/products');
    },

    stripeCheckoutUrl(payload: { price_id: string; company_uniq_id?: string }) {
        return api.post('/api/stripe/checkout/url', payload);
    },

    transactions() {
        return api.get('/api/transactions');
    },

    // Logs (Fil d'actualité / Historique d'activités)
    getLogs<T = unknown>(params?: { page?: number; length?: number; search?: string }) {
        return api.get<T>('/api/logs', { params });
    },

    // Companies
    getAllCompanies(params?: { paginate?: boolean; page?: number; per_page?: number; search?: string; exclude?: string }) {
        return api.get('/api/companies', { params });
    },

    getCompany<T = unknown>(companyId: string | number) {
        return api.get<ApiResponse<T>>(`/api/companies/${companyId}`);
    },

    getCompanyUsers<T = unknown>(companyId: string | number, params?: { paginate?: boolean; page?: number; per_page?: number; search?: string }) {
        return api.get<ApiResponse<T>>(`/api/companies/${companyId}/users`, { params });
    },

    getCompanyTransactions<T = unknown>(companyId: string | number, params?: { paginate?: boolean; export?: boolean; page?: number; per_page?: number; search?: string; date_from?: string; date_to?: string; user_ids?: string }) {
        return api.get<ApiResponse<T>>(`/api/companies/${companyId}/transactions`, { params });
    },

    getCompanyClients<T = unknown>(companyId: string | number) {
        return api.get<ApiResponse<T>>(`/api/companies/${companyId}/clients`);
    },

    getCompanyLogo(companyId: string | number) {
        return api.get(`/api/companies/${companyId}/logo`, { responseType: 'blob' });
    },

    updateCompanyLogo<T = unknown>(companyId: string | number, payload: { file_name: string; base64: string }) {
        return api.patch<ApiResponse<T>>(`/api/companies/${companyId}/logo`, payload);
    },

    updateCompanyUserPassword(userId: string | number, payload: { new_password: string; confirm_password: string }) {
        return api.patch(`/api/companies/users/${userId}/update-password`, payload);
    },
    updateAlertLowBalanceToken(companyId: string | number, payload: { email: string | null; threshold: number | null }) {
        return api.patch(`/api/companies/${companyId}/settings/alert-low-balance-token`, payload);
    },


    publicTokenBalance(uid: string | number, payload: { amount: number }) {
        return api.post(`/api/public/${uid}/account/token-balance`, payload);
    },

    // Company manager (gérants)
    managerGetUser(id: string | number) {
        return api.get(`/api/company-manager/users/${id}`);
    },

    managerUpdateUser(id: string | number, payload: { first_name?: string; last_name?: string }) {
        return api.patch(`/api/company-manager/users/${id}`, payload);
    },

    managerDeleteUser(id: string | number) {
        return api.delete(`/api/company-manager/users/${id}`);
    },

    // Procedure Templates
    procedureTemplates: {
        list(params?: { search?: string; per_page?: number; page?: number }) {
            return api.get('/api/procedure-templates', { params });
        },

        listAdmin(params?: { search?: string }) {
            return api.get('/api/procedure-templates/admin', { params });
        },

        get(templateId: string, params?: { to_load?: string[] }) {
            return serverApi.get(`/procedure-templates/${templateId}`, { params });
        },

        archives() {
            return serverApi.get('/gateway/api/procedure-templates/archives');
        },

        restore(templateId: string) {
            return serverApi.patch(`/gateway/api/procedure-templates/${templateId}/restore`);
        },

        create(payload: {
            name: string;
            description?: string;
            color: string;
            configuration: {
                sections: Array<{
                    id: number;
                    name: string;
                    description: string;
                    fields: Array<{
                        id: number;
                        type: string;
                        label: string;
                        required: boolean;
                        options?: string[];
                        instructions?: string;
                    }>;
                }>;
            };
        }) {
            return serverApi.post('/procedure-templates', payload);
        },

        update(templateId: string, payload: {
            name?: string;
            description?: string;
            color?: string;
            configuration?: {
                sections: Array<{
                    id: number;
                    name: string;
                    description: string;
                    fields: Array<{
                        id: number;
                        type: string;
                        label: string;
                        required: boolean;
                        options?: string[];
                        instructions?: string;
                    }>;
                }>;
            };
        }) {
            return serverApi.patch(`/procedure-templates/${templateId}`, payload);
        },

        archive(templateId: string) {
            return serverApi.patch(`/procedure-templates/${templateId}/archive`);
        },

        duplicate(templateId: string, payload: { selected_companies: Array<{ company_id: string; user_ids?: string[] }> }) {
            return serverApi.post(`/procedure-templates/${templateId}/duplicate`, payload);
        },

        toggleLock(templateId: string) {
            return serverApi.patch(`/procedure-templates/${templateId}/toggle-lock`);
        },

        getReports(templateId: string, params?: { page?: number; per_page?: number; search?: string; days?: number; users?: string[]; company_ids?: string[] }) {
            return serverApi.get(`/procedure-templates/${templateId}/reports`, { params });
        },

        getReport(templateId: string, reportId: string) {
            return serverApi.get(`/procedure-templates/${templateId}/reports/${reportId}`);
        },

        getCollaborators(templateId: string) {
            return serverApi.get(`/procedure-templates/${templateId}/collaborators`);
        },

        updateCollaborators(templateId: string, payload: { all_users: boolean; users: string[]; companies: string[] }) {
            return serverApi.patch(`/procedure-templates/${templateId}/collaborators`, payload);
        },
    },

    // Procedure Reports (rapports de procédures)
    procedureReportsList(params?: { 
        search?: string; 
        status?: 'draft' | 'completed';
        per_page?: number; 
        page?: number 
    }) {
        return api.get('/api/procedure-reports', { params });
    },

    procedureReportsGet(id: string) {
        return api.get(`/api/procedure-reports/${id}`);
    },

    // === Procedure Invitations (gérant) ===
    
    /**
     * Liste les invitations pour un modèle de procédure
     */
    listProcedureInvitations(procedureUniqId: string) {
        return api.get(`/api/procedure-templates/${procedureUniqId}/invitations`);
    },

    /**
     * Crée une nouvelle invitation pour un rapport de procédure
     */
    createProcedureInvitation(procedureUniqId: string, payload: {
        email: string;
        folder_id: string;
        parent_subfolder_id?: string;
        expires_in_days?: number;
        is_otp_required?: boolean;
    }) {
        return api.post(`/api/procedure-templates/${procedureUniqId}/invite`, payload);
    },

    /**
     * Renvoie l'email d'invitation
     */
    resendInvitationEmail(procedureUniqId: string, invitationId: string) {
        return api.post(`/api/procedure-templates/${procedureUniqId}/invitations/${invitationId}/send-email`);
    },

    /**
     * Met à jour la date d'expiration d'une invitation
     */
    updateInvitationExpiration(procedureUniqId: string, invitationId: string, payload: {
        expires_at: string;
    }) {
        return api.patch(`/api/procedure-templates/${procedureUniqId}/invitations/${invitationId}/expiration`, payload);
    },

    /**
     * Révoque une invitation (la désactive)
     */
    revokeInvitation(procedureUniqId: string, invitationId: string) {
        return api.patch(`/api/procedure-templates/${procedureUniqId}/invitations/${invitationId}/revoke`);
    },

    /**
     * Active une invitation (la réactive après révocation)
     */
    activateInvitation(procedureUniqId: string, invitationId: string) {
        return api.patch(`/api/procedure-templates/${procedureUniqId}/invitations/${invitationId}/activate`);
    },

    /**
     * Supprime une invitation
     */
    deleteInvitation(procedureUniqId: string, invitationId: string) {
        return api.delete(`/api/procedure-templates/${procedureUniqId}/invitations/${invitationId}`);
    },

    // === Procedure Invitations publiques (invité) ===
    
    /**
     * Récupère le contexte d'une invitation publique (sans auth)
     */
    getPublicInvitation(uuid: string) {
        console.log('[API] getPublicInvitation called, uuid:', uuid);
        return api.get(`/api/public/procedures/invitations/${uuid}`);
    },

    /**
     * Demande l'envoi d'un code OTP pour une invitation
     */
    requestInvitationOtp(uuid: string) {
        console.log('[API] requestInvitationOtp called, uuid:', uuid);
        return api.post(`/api/public/procedures/invitations/${uuid}/otp-request`);
    },

    /**
     * Vérifie le code OTP et authentifie l'invité
     * Le token Sanctum est retourné dans un cookie HTTP-only (horodaty_guest_token)
     * géré automatiquement par le navigateur
     */
    verifyInvitationOtp(uuid: string, otp: string) {
        console.log('[API] verifyInvitationOtp called, uuid:', uuid, 'otp:', otp);
        return api.post<{ status: string; user: any; invitation_uniq_id: string }>(
            `/api/public/procedures/invitations/${uuid}/otp-login`, 
            { otp },
            { withCredentials: true } // Important: permet de recevoir et stocker le cookie
        );
    },

    // === Routes Guest authentifiées (après OTP) ===
    // Ces routes utilisent le cookie HTTP-only horodaty_guest_token
    // L'invitation_uuid est dans l'URL, plus besoin de query param

    /**
     * Récupère les données de l'intervention pour un invité authentifié
     */
    getGuestIntervention(invitationUuid: string) {
        console.log('[API] getGuestIntervention called, invitationUuid:', invitationUuid);
        return api.get(`/api/public/procedures/invitations/${invitationUuid}/intervention`, {
            withCredentials: true,
        });
    },

    /**
     * Met à jour les réponses du rapport de procédure (sauvegarde brouillon)
     */
    updateGuestReport(
        invitationUuid: string,
        payload: {
            answers: {
                sections: Array<{
                    id: string | number;
                    fields: Array<{
                        id: string | number;
                        type: string;
                        value: string | string[] | null;
                    }>;
                }>;
            };
        }
    ) {
        console.log('[API] updateGuestReport called, invitationUuid:', invitationUuid);
        return api.patch(`/api/public/procedures/invitations/${invitationUuid}/intervention`, payload, {
            withCredentials: true,
        });
    },

    /**
     * Finalise le rapport de procédure d'un invité
     */
    finalizeGuestReport(
        invitationUuid: string,
        payload: {
            answers: {
                sections: Array<{
                    id: string | number;
                    fields: Array<{
                        id: string | number;
                        type: string;
                        value: string | string[] | null;
                    }>;
                }>;
            };
        }
    ) {
        console.log('[API] finalizeGuestReport called, invitationUuid:', invitationUuid);
        return api.patch(`/api/public/procedures/invitations/${invitationUuid}/intervention/finalize`, payload, {
            withCredentials: true,
        });
    },

    /**
     * Upload une photo OU une vidéo pour le rapport d'un invité.
     * Pour une photo : fournir `base64`. Pour une vidéo : fournir `video` (Blob/File).
     * Les jetons d'horodatage sont débités sur l'entreprise du créateur de l'invitation.
     */
    uploadGuestPicture(invitationUuid: string, payload: MediaUploadPayload) {
        console.log('[API] uploadGuestPicture called, invitationUuid:', invitationUuid);
        return postMedia(
            `/api/public/procedures/invitations/${invitationUuid}/intervention/pictures`,
            payload,
            { withCredentials: true }
        );
    },

    /**
     * Déconnecte un invité (révoque le token et supprime le cookie)
     */
    logoutGuest(userId: string) {
        return api.post(`/api/public/${userId}/logout`, {}, {
            withCredentials: true,
        });
    },

    /**
     * Recherche globale - appelle les 4 endpoints en parallèle
     * @param query - Terme de recherche
     * @param limit - Nombre de résultats par catégorie (défaut: 5)
     * @returns Résultats groupés par catégorie avec erreurs éventuelles par section
     */
    async globalSearch(query: string, limit: number = 5) {
        const [foldersRes, picturesRes, templatesRes, reportsRes] = await Promise.allSettled([
            api.get('/api/folders', { 
                params: { search: query, per_page: limit } 
            }),
            api.get('/api/pictures/paginate', { 
                params: { search: query, per_page: limit } 
            }),
            api.get('/api/procedure-templates', { 
                params: { search: query, per_page: limit } 
            }),
            api.get('/api/procedure-reports', { 
                params: { search: query, per_page: limit } 
            }),
        ]);

        const extractResult = <T>(result: PromiseSettledResult<any>, dataPath: 'data' | 'templates' = 'data'): { data: T[]; error: string | null } => {
            if (result.status === 'fulfilled') {
                const response = result.value.data;
                let responseData: T[] | undefined;
                
                if (dataPath === 'templates') {
                    responseData = response?.templates;
                } else {
                    // Essayer différentes structures de réponse
                    // 1. Structure paginée Laravel: { data: { data: [...], current_page, ... } }
                    // 2. Structure simple paginée: { data: [...], current_page, ... }
                    // 3. Structure wrapper: { data: [...] }
                    // 4. Tableau direct: [...]
                    if (response?.data?.data && Array.isArray(response.data.data)) {
                        responseData = response.data.data;
                    } else if (response?.data && Array.isArray(response.data)) {
                        responseData = response.data;
                    } else if (Array.isArray(response)) {
                        responseData = response;
                    }
                }
                return { data: responseData || [], error: null };
            }
            const errorMessage = result.reason?.response?.data?.message 
                || result.reason?.message 
                || 'Une erreur est survenue';
            return { data: [], error: errorMessage };
        };

        return {
            folders: extractResult(foldersRes).data,
            foldersError: extractResult(foldersRes).error,
            pictures: extractResult(picturesRes).data,
            picturesError: extractResult(picturesRes).error,
            templates: extractResult(templatesRes, 'templates').data,
            templatesError: extractResult(templatesRes, 'templates').error,
            reports: extractResult(reportsRes).data,
            reportsError: extractResult(reportsRes).error,
        };
    },

    // ===== V2 - Endpoints optimisés pour horodaty-v2 =====

    /**
     * Endpoint unifié - détecte automatiquement si folder, subfolder, ou 'root'
     * Utiliser id='root' pour le listing des dossiers racine avec filtres
     * 
     * @see GET /api/v2/document-tree/directories/{directory_id}/content
     */
    getDocumentTreeDirectoryContent(
        id: string | number,
        params?: {
            pictures_limit?: number;
            // Paramètres de filtre (uniquement pour id='root')
            company_ids?: string[];
            days?: number;
            users?: string[];
            search?: string;
            include_archived?: number;
            // Paramètres de tri
            sort_by?: 'name' | 'updated_at' | 'created_at';
            sort_direction?: 'asc' | 'desc';
        }
    ) {
        return api.get<{
            type: 'folder' | 'subfolder' | 'root';
            item: {
                uniq_id: string;
                name: string;
                ref: string;
                address?: string;
                type?: string;
                is_locked?: boolean;
                is_gallery_folder?: boolean;
                folder_uniq_id?: string;
                updated_at: string;
                created_at: string;
            } | null;
            ancestors: Array<{
                uniq_id: string;
                name: string;
                type: string;
            }>;
            gallery_intervention_id: string | null;
            children: Array<{
                uniq_id: string;
                name: string;
                ref: string;
                type: string;
                documents_count?: number;
                // Champs supplémentaires pour type='root' (children = folders)
                external_ref?: string | null;
                street_1?: string | null;
                street_2?: string | null;
                cp?: string | null;
                city?: string | null;
                notes?: string | null;
                is_gallery_folder?: boolean;
                is_locked?: boolean;
                intervention_date?: string | null;
                user_uniq_id?: string | null;
                company_uniq_id?: string | null;
                company_name?: string | null;
                updated_at: string;
                created_at: string;
            }>;
            pictures: Array<{
                uniq_id: string;
                file_name: string;
                name: string;
                mime: string;
                certificate_uniq_id: string | null;
                latitude: number | null;
                longitude: number | null;
                thumbnail_url: string;
                image_url: string;
                created_at: string;
                updated_at: string;
            }>;
            pictures_total: number;
        }>(`/api/v2/document-tree/directories/${id}/content`, { params });
    },

    /**
     * Déplacer un sous-dossier vers un autre emplacement
     * 
     * @see PATCH /api/v2/document-tree/directories/{directory_id}/move
     */
    moveDirectory(directoryId: string, data: { destination_uniq_id: string; destination_type: 'folder' | 'subfolder' }) {
        return api.patch(`/api/v2/document-tree/directories/${directoryId}/move`, data);
    },

    /**
     * Mettre à jour le statut d'un dossier ou sous-dossier (open, closed, ou null)
     *
     * @see PATCH /api/v2/document-tree/directories/{directory_id}/status
     */
    updateDirectoryStatus(directoryId: string, status: string | null) {
        return api.patch(`/api/v2/document-tree/directories/${directoryId}/status`, { status });
    },

    /**
     * Récupérer les éléments récemment modifiés (pour le dashboard)
     * 
     * @see GET /api/v2/document-tree/recent
     */
    getDocumentTreeRecent(limit: number = 6) {
        return api.get<{
            items: Array<{
                uniq_id: string;
                name: string;
                ref: string;
                type: string; // 'folder', 'procedure', etc.
                folder_uniq_id: string | null;
                parent_subfolder_uniq_id: string | null;
                updated_at: string;
            }>;
        }>('/api/v2/document-tree/recent', { params: { limit } });
    },

    // ===== Subsidiaries (sous-comptes) =====

    subsidiaries: {
        /**
         * Lister les sous-comptes d'une entreprise
         * @param companyId - uniq_id de l'entreprise parente
         * @param params - Paramètres optionnels (pagination, recherche, descendants)
         */
        list(companyId: string, params?: { paginate?: boolean; page?: number; per_page?: number; search?: string; include_descendants?: boolean }) {
            return api.get(`/api/companies/${companyId}/childrens`, { params });
        },

        /**
         * Créer un sous-compte
         * @param companyId - uniq_id de l'entreprise parente
         */
        create(companyId: string, payload: {
            company: {
                name: string;
                street: string;
                cp: string;
                city: string;
                siret: string;
                email: string;
                description?: string;
                notes?: string;
                phone?: string;
                authorized_buy_tokens: boolean;
            };
            owner?: {
                firstname: string;
                lastname: string;
                email: string;
            };
        }) {
            return api.post(`/api/companies/${companyId}/childrens`, payload);
        },

        /**
         * Récupérer les détails d'un sous-compte
         */
        get(companyId: string, childId: string) {
            return api.get(`/api/companies/${companyId}/childrens/${childId}`);
        },

        /**
         * Lister les utilisateurs d'un sous-compte
         */
        getUsers(companyId: string, childId: string, params?: { paginate?: boolean; page?: number; per_page?: number; search?: string }) {
            return api.get(`/api/companies/${companyId}/childrens/${childId}/users`, { params });
        },

        /**
         * Créer un utilisateur dans un sous-compte
         */
        createUser(companyId: string, childId: string, payload: {
            first_name: string;
            last_name: string;
            email: string;
            role?: string;
        }) {
            return api.post(`/api/companies/${companyId}/childrens/${childId}/users`, payload);
        },

        /**
         * Récupérer les transactions d'un sous-compte
         */
        getTransactions(companyId: string, childId: string) {
            return api.get(`/api/companies/${companyId}/childrens/${childId}/transactions`);
        },

        /**
         * Transférer des crédits (jetons) vers un sous-compte
         */
        transferCredits(companyId: string, childId: string, credits: number) {
            return api.patch(`/api/companies/${companyId}/childrens/${childId}/transfer-credits`, { credits });
        },

        /**
         * Mettre à jour les informations d'un sous-compte
         */
        update(companyId: string, childId: string, payload: {
            nom: string;
            description?: string;
            notes?: string;
            rue: string;
            code_postal: string;
            ville: string;
            siret: string;
            tva?: string;
            phone?: string;
            email: string;
            billing_email?: string;
        }) {
            return api.patch(`/api/companies/${companyId}/childrens/${childId}`, payload);
        },

        /**
         * Mettre à jour les informations du propriétaire (particulier) d'un sous-compte
         */
        updateOwner(companyId: string, childId: string, payload: {
            first_name: string;
            last_name: string;
            email: string;
        }) {
            return api.patch(`/api/companies/${companyId}/childrens/${childId}`, payload);
        },

        /**
         * Mettre à jour l'autorisation d'achat de jetons d'un sous-compte
         */
        updateBuyTokensAuth(companyId: string, childId: string, authorized: boolean) {
            return api.patch(
                `/api/companies/${companyId}/childrens/${childId}/settings/authorization-buy-tokens`,
                { authorized_buy_tokens: authorized },
            );
        },

    },

    /**
     * @deprecated Utiliser horodatyApi.createProcedureIntervention() à la place
     * La création de rapports se fait via POST /api/folders/{folder_id}/interventions avec type: 'procedure'
     */
    procedureReports: {
        /**
         * @deprecated Cette route n'existe pas côté backend.
         * Utiliser horodatyApi.createProcedureIntervention(folderId, payload) à la place.
         */
        create(payload: {
            template_uniq_id: string;
            folder_uid: string;
            data: Record<string, any>;
        }) {
            console.warn(
                '[DEPRECATED] procedureReports.create() is deprecated. ' +
                'Use horodatyApi.createProcedureIntervention() instead.'
            );
            // Rediriger vers la bonne méthode si possible
            throw new Error(
                'Cette route n\'existe pas. Utilisez horodatyApi.createProcedureIntervention(folderId, payload)'
            );
        }
    },

    devices: {
        list(params?: { page?: number; per_page?: number; order_by?: string; order?: string }) {
            return api.get('/api/user/devices', { params });
        },
        delete(id: string | number) {
            return api.delete(`/api/user/devices/${id}`);
        }
    },

    adminParameters: {
        get() {
            return api.get('/api/user/settings/admin-parameters');
        },
        getLegalLinks() {
            return api.get('/api/admin-parameters/legal-links');
        },
        update(accountType: 'company' | 'user', payload: {
            welcome_token_count?: number;
            restitution_delay_days?: number;
            card_payment_enabled?: boolean;
            wire_transfer_enabled?: boolean;
            cgu_url?: string | null;
            privacy_policy_url?: string | null;
        }) {
            return api.put(`/api/admin-parameters/${accountType}`, payload);
        },
    },
};
